import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosProgressEvent,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import getApiBase from "../utils/apiBase";
import { readNdjson } from "../utils/ndjson";
import {
  createAccessTokenRenewer,
  type AccessTokenRenewer,
} from "./auth/accessTokenRenewal";
import { getActiveWorkspaceId } from "./workspace/activeWorkspace";
import { getEmbedToken } from "./embed/embedToken";
import { safeLocalStorage } from "../utils/safeStorage";
import type {
  MagicLinkConsumeRequest,
  MagicLinkRequest,
} from "./auth/magicLink.types";
import type {
  KgDataSourceReportsResponse,
  KgDataSourceTablesResponse,
  KgDataSourceUsedInResponse,
  KgNodeOverviewResponse,
  KgOntologyLinksResponse,
  KgOntologyMetricsResponse,
  KgOntologyOverviewResponse,
  KgOntologyReportsResponse,
  KgOntologyRulesResponse,
  KgMetricFormulaResponse,
  KgMetricOverviewResponse,
  KgDataStoryActivityResponse,
  KgDataStoryOverviewResponse,
  KgDataStoryTraceResponse,
  KgArtifactActivityResponse,
  KgArtifactOverviewResponse,
  KgDataModelMetricsResponse,
  KgDataModelOverviewResponse,
  KgDataModelRelationshipsResponse,
  KgDataModelTablesResponse,
  KgReportActivityResponse,
  KgReportOverviewResponse,
  KgReportTraceResponse,
  KgSchemaMetricsResponse,
  KgSchemaRelationshipsParams,
  KgSchemaRelationshipsResponse,
  KgSchemaUsedInParams,
  KgTableOverviewResponse,
} from "./kg/kgDrawerApi.types";
import type {
  KgEntityGraphResponse,
  KgGraphEntitiesParams,
} from "./kg/kgGraphApi.types";

/**
 * Which API serves a route both have: "v1", the old backend, or "v2",
 * api-service, which answers it with the same contract for a Story on the new
 * path. A builder that takes one defaults to "v1".
 */
export type ApiVersion = "v1" | "v2";

export const API_ENDPOINTS = {
  agents: {
    /** List and create: agents belong to a workspace and keep their creator. */
    list: (workspaceId: string) => `/api/v1/workspaces/${workspaceId}/agents`,
    create: (workspaceId: string) => `/api/v1/workspaces/${workspaceId}/agents`,
    update: (workspaceId: string, agentId: string) =>
      `/api/v1/workspaces/${workspaceId}/agents/${agentId}`,
  },
  home: {
    sections: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/home/sections`,
    artifacts: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/home/artifacts`,
    /** POST { kind: "like"|"save", on } → { liked, saved, likes } */
    itemReactions: (itemId: string) => `/api/v1/home/items/${encodeURIComponent(itemId)}/reactions`,
    /** POST → 204; one view per user per item per UTC day */
    itemView: (itemId: string) => `/api/v1/home/items/${encodeURIComponent(itemId)}/view`,
    /** GET { users, workspaces }; PUT { target_type, target_id, on } → { following } */
    follows: "/api/v1/home/follows",
    /** GET { text, audio_url, provider } */
    briefingAudio: "/api/v1/home/briefing/audio",
  },
  users: {
    search: "/api/v1/workspaces/users",
    /** GET /api/v1/users/:userId — fetch a single user (NEW backend contract; see services/users/) */
    byId: (userId: string) => `/api/v1/users/${userId}`,
  },
  auth: {
    signup: "/api/v1/auth/signup",
    verifyEmail: "/api/v1/auth/verify-email",
    sendVerification: "/api/v1/auth/send-verification",
    login: "/api/v1/auth/login",
    /** POST { email } finds the organization sign-in URL for an email domain (404 when none). */
    ssoDiscover: "/api/v1/auth/sso/discover",
    forgotPassword: "/api/v1/auth/forgot-password",
    resetPassword: "/api/v1/auth/reset-password",
    logout: "/api/v1/auth/logout",
    /** GET /api/v2/auth/tenants — active tenant memberships for the bearer. */
    tenants: "/api/v2/auth/tenants",
    /** POST /api/v2/auth/select-tenant — mint a token scoped to a chosen tenant. */
    selectTenant: "/api/v2/auth/select-tenant",
    /** POST { email } — email a one-time sign-in link (see services/auth/magicLink.types.ts). */
    magicLink: "/api/v1/auth/magic-link",
    /** POST { token } — exchange a sign-in link for a session (same response as login). */
    magicLinkConsume: "/api/v1/auth/magic-link/consume",
    /** GET — the bearer's UserPublic (enveloped); `is_superadmin` drives admin-only UI. */
    me: "/api/v1/auth/me",
    /** POST { current_password, new_password } — change the bearer's own password. */
    /** POST multipart `file` (png/jpeg/webp, 2 MB) / DELETE — the bearer's own profile photo. */
    avatar: "/api/v1/auth/me/avatar",
    changePassword: "/api/v1/auth/change-password",
    /** POST { ttl_days 1..365 } — mint an MCP access token (stateless JWT, shown once). */
    mcpToken: "/api/v2/auth/mcp-token",
  },
  betaFeatures: {
    /** GET { allowed: string[] } — beta feature keys the organization lets members turn on (any member). */
    allowed: "/api/v2/tenant/beta-features",
    /** GET / PUT { value: string[] } / DELETE — the admin's allow-list, a tenant setting. */
    allowList: "/api/v2/tenant/settings/ui.beta_features_allowed",
  },
  platformAdmin: {
    /** GET ?tenant_id= — every key definition with its platform and tenant level. */
    secrets: "/api/v2/platform/secrets",
    /** PUT {value} / DELETE — platform-wide level. */
    secret: (keyName: string) => `/api/v2/platform/secrets/${encodeURIComponent(keyName)}`,
    /** PUT {value} / DELETE — one tenant's override (422 when the key is not overridable). */
    tenantSecret: (tenantId: string, keyName: string) =>
      `/api/v2/platform/tenants/${tenantId}/secrets/${encodeURIComponent(keyName)}`,
    /** POST {tenant_id?, value?} — a cheap provider call; 429 beyond 5 a minute. */
    secretTest: (keyName: string) => `/api/v2/platform/secrets/${encodeURIComponent(keyName)}/test`,
    /** GET ?feature=&service=&q= */
    prompts: "/api/v2/platform/io/prompts",
    /** GET {prompt, versions} */
    prompt: (name: string) => `/api/v2/platform/io/prompts/${encodeURIComponent(name)}`,
    /** POST {content, note?} — the next version, not active. */
    promptVersions: (name: string) => `/api/v2/platform/io/prompts/${encodeURIComponent(name)}/versions`,
    /** PUT {version} */
    promptActive: (name: string) => `/api/v2/platform/io/prompts/${encodeURIComponent(name)}/active-version`,
    /** POST — activates the shipped default version. */
    promptReset: (name: string) => `/api/v2/platform/io/prompts/${encodeURIComponent(name)}/reset`,
    /** POST {version, variables} — placeholder substitution only, no model call. */
    promptRender: (name: string) => `/api/v2/platform/io/prompts/${encodeURIComponent(name)}/render`,
    /** POST {tenant_id, inputs, inputs_class} — 202 {workflow_id}; 409 not_testable_yet. */
    promptTest: (name: string, version: number) =>
      `/api/v2/platform/io/prompts/${encodeURIComponent(name)}/versions/${version}/tests`,
    /** GET — poll a test's state. */
    promptTestState: (workflowId: string) => `/api/v2/platform/io/tests/${encodeURIComponent(workflowId)}`,
  },
  adminSignups: {
    /** GET ?status=&limit=&offset= — superadmin list (see services/adminSignups/adminSignups.types.ts). */
    list: "/api/v1/admin/signups",
    approve: (requestId: string) => `/api/v1/admin/signups/${requestId}/approve`,
    deny: (requestId: string) => `/api/v1/admin/signups/${requestId}/deny`,
    terminate: (requestId: string) => `/api/v1/admin/signups/${requestId}/terminate`,
  },
  presentationThemes: {
    /** GET — theme catalog: the per-theme folders on GCS the deck renderer uses */
    list: "/api/v1/presentation-themes",
  },
  googleDriveExport: {
    /** POST multipart (file, name, target) — upload converted to Google Slides/Docs, or the consent URL */
    upload: "/api/v1/exports/google-drive",
  },
  /**
   * A Story's data app. Contract: services/dataApps/dataApp.types.ts. Create answers 202 and
   * `latest` is polled while generating; `draw` waits briefly and returns pending blocks to re-ask.
   */
  dataApps: {
    /** POST → 202 { data_app_id, status } */
    create: (storyId: string) => `/api/v2/story/${storyId}/data-apps`,
    /** GET — the newest version, generating or ready */
    latest: (storyId: string) => `/api/v2/story/${storyId}/data-apps/latest`,
    /** POST — draw blocks for the viewer's control values */
    draw: (storyId: string, dataAppId: string) =>
      `/api/v2/story/${storyId}/data-apps/${dataAppId}/draw`,
    /** POST — the SQL behind one block: readable for every viewer, the executed text on request for managers */
    blockSql: (storyId: string, dataAppId: string, blockId: string) =>
      `/api/v2/story/${storyId}/data-apps/${dataAppId}/blocks/${blockId}/sql`,
    /** POST — draw one chart block with a look the person is trying (the editor's preview); nothing is saved */
    chartPreview: (storyId: string, dataAppId: string, blockId: string) =>
      `/api/v2/story/${storyId}/data-apps/${dataAppId}/blocks/${blockId}/chart-preview`,
    /** GET → { versions } newest first */
    versions: (storyId: string) => `/api/v2/story/${storyId}/data-apps`,
    /** GET — one version, by its row id */
    version: (storyId: string, dataAppId: string) => `/api/v2/story/${storyId}/data-apps/${dataAppId}`,
    /** POST → 202 { data_app_id, status } — a chat edit, direct edits or a restore of this version */
    edits: (storyId: string, dataAppId: string) => `/api/v2/story/${storyId}/data-apps/${dataAppId}/edits`,
    /** POST → 202 — stop the running upgrade of this (starter) version; 409 when none is running */
    cancelUpgrade: (storyId: string, dataAppId: string) =>
      `/api/v2/story/${storyId}/data-apps/${dataAppId}/cancel-upgrade`,
    /** POST → 202 { version } — stop the run writing this version (edit, restore, build, improve); owner only; 409 DATA_APP_NOT_RUNNING once it finished */
    cancel: (storyId: string, dataAppId: string) => `/api/v2/story/${storyId}/data-apps/${dataAppId}/cancel`,
    /** GET { events, done } — the version's progress log; pass `after` (a seq) as a query param */
    events: (storyId: string, dataAppId: string) => `/api/v2/story/${storyId}/data-apps/${dataAppId}/events`,
    /** POST { quality? } → 202 { data_app_id } — design an upgrade of this ready version; 409 while one is being made */
    improve: (storyId: string, dataAppId: string) => `/api/v2/story/${storyId}/data-apps/${dataAppId}/improve`,
    /**
     * What is made from a version (deck, memo, video). Contract: services/dataApps/dataAppOutputs.types.ts.
     * POST { kinds, picks, options } → 202 { request_id, outputs }; GET → { outputs } newest first
     */
    outputs: (storyId: string, dataAppId: string) => `/api/v2/story/${storyId}/data-apps/${dataAppId}/outputs`,
    /** GET { events, done } — one output's progress log; pass `after` (a seq) as a query param */
    outputEvents: (storyId: string, outputId: string) => `/api/v2/story/${storyId}/data-apps/outputs/${outputId}/events`,
    /** GET { url, expires_at, filename } — a signed link to one file of a ready output */
    outputFile: (storyId: string, outputId: string, filename: string) =>
      `/api/v2/story/${storyId}/data-apps/outputs/${outputId}/files/${encodeURIComponent(filename)}`,
    /** POST → 202 — stop an output that is queued or running */
    cancelOutput: (storyId: string, outputId: string) => `/api/v2/story/${storyId}/data-apps/outputs/${outputId}/cancel`,
  },
  /**
   * Personalized distribution — story owner only. Contract and route list:
   * services/distributions/distributions.types.ts. Writes answer 202 and are polled on `edit`.
   */
  distributions: {
    /** GET list / POST create */
    list: (storyId: string) => `/api/v2/story/${storyId}/distributions`,
    /** GET — status of an accepted write */
    edit: (storyId: string, requestId: string) =>
      `/api/v2/story/${storyId}/distributions/edits/${requestId}`,
    /** POST — which tiles personalize for a split field (no query runs) */
    splitCheck: (storyId: string) => `/api/v2/story/${storyId}/distributions/split-check`,
    /** POST → 202 { request_id } */
    valueStats: (storyId: string) => `/api/v2/story/${storyId}/distributions/value-stats`,
    /** GET — polled until `done` */
    valueStatsResult: (storyId: string, requestId: string) =>
      `/api/v2/story/${storyId}/distributions/value-stats/${requestId}`,
    /** POST — the next sends of a schedule, in its time zone */
    schedulePreview: (storyId: string) =>
      `/api/v2/story/${storyId}/distributions/schedule-preview`,
    /** GET ?audience=inside|outside → { items: SplitFieldOptionApi[] } — fields the story can be split by */
    splitFields: (storyId: string) => `/api/v2/story/${storyId}/distributions/split-fields`,
    /** GET / PATCH / DELETE */
    detail: (storyId: string, distributionId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}`,
    /** POST { status: active | paused } */
    status: (storyId: string, distributionId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/status`,
    /** POST → 202 — an archived distribution comes back paused (if ever activated) or as a draft */
    restore: (storyId: string, distributionId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/restore`,
    /** PUT schedule | null */
    schedule: (storyId: string, distributionId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/schedule`,
    /** GET ?offset&limit / PUT full replace */
    recipients: (storyId: string, distributionId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/recipients`,
    /**
     * POST → 202 — re-runs the live checks (Slack app membership) on the stored rows.
     * Proposed in services/distributions/handoff/chunk1-a.md; not in the contract yet.
     */
    recipientsRecheck: (storyId: string, distributionId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/recipients/recheck`,
    /** POST → LiveTilesApi (one draw of every data tile for a value); GET …/{drawId} re-asks for the running ones. */
    liveTiles: (storyId: string, distributionId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/live-tiles`,
    liveTilesDraw: (storyId: string, distributionId: string, drawId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/live-tiles/${drawId}`,
    /** POST → PreviewRenderApi (a run-free copy preview); GET …/{renderId} re-asks while it runs. */
    previewRender: (storyId: string, distributionId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/preview-render`,
    previewRenderGet: (storyId: string, distributionId: string, renderId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/preview-render/${renderId}`,
    /** GET ?offset&limit / POST → 202 { run_id } */
    runs: (storyId: string, distributionId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/runs`,
    run: (storyId: string, distributionId: string, runId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/runs/${runId}`,
    /** POST → 202 { run_id, delivery_count } | 409 RUN_IN_PROGRESS / NOTHING_TO_RETRY: sends again only what didn't arrive */
    runRetryFailed: (storyId: string, distributionId: string, runId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/runs/${runId}/retry-failed`,
    slice: (storyId: string, distributionId: string, runId: string, sliceId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/runs/${runId}/slices/${sliceId}`,
    /** GET `?format=all|pdf|pptx` → a zip of the run's files (needs the JWT, so fetched as a blob) */
    runFilesZip: (storyId: string, distributionId: string, runId: string) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/runs/${runId}/files.zip`,
    /** GET → a short-lived signed URL */
    sliceFile: (
      storyId: string,
      distributionId: string,
      runId: string,
      sliceId: string,
      format: string,
    ) =>
      `/api/v2/story/${storyId}/distributions/${distributionId}/runs/${runId}/slices/${sliceId}/files/${format}`,
  },
  /** Tenant templates for personalized distribution (built-ins first). */
  distributionTemplates: {
    /** GET list / POST create */
    list: "/api/v2/distribution-templates",
    /** PATCH / DELETE */
    detail: (templateId: string) => `/api/v2/distribution-templates/${templateId}`,
    /**
     * GET — status of an accepted template write. Proposed in
     * services/distributions/handoff/chunk1-a.md: the contract returns a request_id but no route.
     */
    edit: (requestId: string) => `/api/v2/distribution-templates/edits/${requestId}`,
    // ─── Round 7 · uploaded PowerPoint templates (pd7 CONTRACT §2.4) ───
    /** POST multipart/form-data, one `file` field (.pptx or .potx, up to 25 MB) → 201 DistributionMasterUploadApi */
    masters: "/api/v2/distribution-templates/masters",
    /** GET → DistributionMasterLookApi: the upload as action-engine read it (`preparing` until then) */
    master: (masterId: string) => `/api/v2/distribution-templates/masters/${masterId}`,
    // ─── end round 7 · uploaded PowerPoint templates ───
  },
  /** GET → { items: DistributionThemeApi[] } — deck themes a distribution copy can be drawn in. */
  distributionThemes: {
    list: "/api/v2/distribution-themes",
  },
  // ─── Round 6 · brand kits (WP7) ───────────────────────────────────────────────
  /** Tenant brand kits: logos and colour palettes (services/distributions/distributionBrandKits.types.ts). */
  distributionBrandKits: {
    /** GET list / POST create */
    list: "/api/v2/distribution-brand-kits",
    /** GET / PUT (replace) / DELETE (archive) */
    detail: (kitId: string) => `/api/v2/distribution-brand-kits/${kitId}`,
    /** POST → 202: make it the kit new distributions start with */
    setDefault: (kitId: string) => `/api/v2/distribution-brand-kits/${kitId}/default`,
    /** GET — status of an accepted write */
    edit: (requestId: string) => `/api/v2/distribution-brand-kits/edits/${requestId}`,
    /** POST multipart/form-data, one `file` field (PNG or JPEG) → 201 LogoUploadedApi */
    logos: "/api/v2/distribution-brand-kits/logos",
  },
  // ─── end round 6 · brand kits ─────────────────────────────────────────────────
  workspaces: {
    create: "/api/v1/workspaces/",
    detail: (workspaceId: string) => `/api/v1/workspaces/${workspaceId}`,
    connectors: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/data-sources`,
    members: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/members`,
    member: (workspaceId: string, memberId: string) =>
      `/api/v1/workspaces/${workspaceId}/members/${memberId}`,
    linkedDataSources: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/data-sources`,
    dataStories: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/data-stories`,
    filePreview: (workspaceId: string, fileId: string) =>
      `/api/v1/workspaces/${workspaceId}/files/${fileId}/preview`,
    fileDownload: (workspaceId: string, fileId: string) =>
      `/api/v1/workspaces/${workspaceId}/files/${fileId}/download`,
    /** GET /api/v2/tenants/:tenantId/workspaces — caller workspace memberships. */
    forTenant: (tenantId: string) => `/api/v2/tenants/${tenantId}/workspaces`,
  },
  tenants: {
    /** PATCH { name } — tenant admins (or superadmins) rename the organization. */
    item: (tenantId: string) => `/api/v1/tenants/${tenantId}`,
    /** GET (404 when none) / POST (configure) / DELETE the organization's SSO config. */
    sso: (tenantId: string) => `/api/v1/tenants/${tenantId}/sso`,
    /** POST — re-checks the stored IdP metadata / issuer discovery is reachable. */
    ssoTest: (tenantId: string) => `/api/v1/tenants/${tenantId}/sso/test`,
    /** GET — the sign-in URLs the identity provider needs (redirect URI, ACS, entity id, metadata, SCIM base). */
    ssoServiceProvider: (tenantId: string) => `/api/v1/tenants/${tenantId}/sso/service-provider`,
    /** POST — returns the URL that starts a test sign-in for the signed-in admin. */
    ssoTestLogin: (tenantId: string) => `/api/v1/tenants/${tenantId}/sso/test-login`,
    /** POST — checks the DNS TXT record that proves the email domain. */
    ssoVerifyDomain: (tenantId: string) => `/api/v1/tenants/${tenantId}/sso/verify-domain`,
    /** POST generates or rotates (token shown once), DELETE revokes the SCIM token. */
    ssoScimToken: (tenantId: string) => `/api/v1/tenants/${tenantId}/sso/scim-token`,
    /**
     * GET /api/v2/tenants/:tenantId/users?search=&limit= — active tenant
     * members, for the story-invite picker's user search. Deliberately not
     * story-aware (does not exclude people already on the story).
     */
    users: (tenantId: string) => `/api/v2/tenants/${tenantId}/users`,
    /**
     * GET (list) / POST (create) — the tenant's custom Chart Studio colour
     * palettes, shared by every chart and story in the tenant. From the FE
     * guide; not in doc-hub yet.
     */
    colorPalettes: (tenantId: string) => `/api/v2/tenants/${tenantId}/color-palettes`,
  },
  review: {
    /** GET keyset page of review items; always pass `kind`. */
    items: "/api/v2/review-items",
    item: (id: string) => `/api/v2/review-items/${id}`,
    /** POST decisions (202) with an Idempotency-Key header. */
    decisions: "/api/v2/decisions",
    decision: (id: string) => `/api/v2/decisions/${id}`,
  },
  columns: {
    /** GET tenant-wide offset page of columns with counted class, who set it and a state filter. */
    sensitivity: "/api/v2/columns/sensitivity",
  },
  datasets: {
    /** GET offset pages of a catalog dataset's columns with counted class. */
    fields: (datasetId: string) => `/api/v2/datasets/${datasetId}/fields`,
  },
  tenantSettings: {
    /** GET { data: TenantSetting[] } — the settings a tenant admin may edit. */
    list: "/api/v2/tenant/settings",
    /** PUT { value } / DELETE (reset to default) one key. */
    item: (key: string) => `/api/v2/tenant/settings/${encodeURIComponent(key)}`,
  },
  tenantUi: {
    /** GET /api/v2/tenant/ui-settings — readable by any member; `{ canvas_tile_style }`. */
    settings: "/api/v2/tenant/ui-settings",
  },
  modelling: {
    /** GET — where the tenant's data model stands: `{state, release, started_at, reason, error_code, held_run_id, rejection}`. */
    status: "/api/v2/modelling/status",
    /** GET — a held build's changes, grouped `added` / `changed` / `retired`. Tenant admin only. */
    changes: (runId: string) => `/api/v2/modelling/runs/${encodeURIComponent(runId)}/changes`,
    /** POST — approve a held build: Modelling builds and publishes it. Tenant admin only. */
    approve: (runId: string) => `/api/v2/modelling/runs/${encodeURIComponent(runId)}/approve`,
    /** POST — reject a held build: the live model stays and the same change isn't raised again. Tenant admin only. */
    reject: (runId: string) => `/api/v2/modelling/runs/${encodeURIComponent(runId)}/reject`,
  },
  invites: {
    /** GET /api/v2/invites/:token — public, unauthenticated preview shown
     * before signup ("Jane invited you to collaborate on X"). */
    preview: (token: string) => `/api/v2/invites/${token}`,
    /** POST /api/v2/invites/:token/accept — creates the invitee's first
     * tenant + story membership and returns a real session token. Also
     * public/token-authenticated — the caller has no membership yet. */
    accept: (token: string) => `/api/v2/invites/${token}/accept`,
  },
  /**
   * Data Studio v2 asset APIs. Identity is `(connectionId, assetId[, columnId])`
   * — there is no `workspace_id` on any v2 path.
   */
  dataStudio: {
    /** Tenant-wide asset search, cross-connector. */
    assetSearch: "/api/v2/assets",
    /** Single asset with type-specific `attributes`. */
    asset: (connectionId: string, assetId: string) =>
      `/api/v2/connectors/${connectionId}/assets/${assetId}`,
    assetColumns: (connectionId: string, assetId: string) =>
      `/api/v2/connectors/${connectionId}/assets/${assetId}/columns`,
    /** GET column detail, PATCH curated fields. */
    assetColumn: (connectionId: string, assetId: string, columnId: string) =>
      `/api/v2/connectors/${connectionId}/assets/${assetId}/columns/${columnId}`,
    assetRecommendations: (connectionId: string, assetId: string) =>
      `/api/v2/connectors/${connectionId}/assets/${assetId}/recommendations`,
    assetRows: (connectionId: string, assetId: string) =>
      `/api/v2/connectors/${connectionId}/assets/${assetId}/rows`,
  },
  /** api-service connectors: the v2 wizard, list and detail (`services/connectorsV2`). */
  connectorsV2: {
    types: "/api/v2/connector-types",
    typeNotify: (key: string) => `/api/v2/connector-types/${key}/notify`,
    sourceRequests: "/api/v2/connector-requests",
    list: "/api/v2/connectors",
    dailyRuns: "/api/v2/connectors/runs/daily",
    runs: (id: string) => `/api/v2/connectors/${id}/runs`,
    usedIn: (id: string) => `/api/v2/connectors/${id}/used-in`,
    item: (id: string) => `/api/v2/connectors/${id}`,
    credentials: (id: string) => `/api/v2/connectors/${id}/credentials`,
    onboarding: (id: string) => `/api/v2/connectors/${id}/onboarding`,
    ingestionState: (id: string) => `/api/v2/connectors/${id}/ingestion-state`,
    datasets: (id: string) => `/api/v2/connectors/${id}/datasets`,
    selection: (id: string) => `/api/v2/connectors/${id}/selection`,
    refresh: (id: string) => `/api/v2/connectors/${id}/refresh`,
    syncs: (id: string) => `/api/v2/connectors/${id}/syncs`,
    uploads: (id: string) => `/api/v2/connectors/${id}/uploads`,
    upload: (id: string, uploadId: string) => `/api/v2/connectors/${id}/uploads/${uploadId}`,
    uploadComplete: (id: string, uploadId: string) =>
      `/api/v2/connectors/${id}/uploads/${uploadId}/complete`,
    uploadModel: (id: string, uploadId: string) => `/api/v2/connectors/${id}/uploads/${uploadId}/model`,
    driftEvents: (id: string) => `/api/v2/connectors/${id}/drift-events`,
    driftResolution: (id: string, eventId: string) =>
      `/api/v2/connectors/${id}/drift-events/${eventId}/resolution`,
  },
  connectorConnections: {
    list: "/api/v1/workspaces/connector-connections",
  },
  connectors: {
    list: "/api/v1/connectors/",
    schema: (key: string) => `/api/v1/connectors/${key}/schema`,
    connect: (slug: string) => `/api/v1/connectors/${slug}/connect`,
    oauthAuthorize: (slug: string) =>
      `/api/v1/connectors/${slug}/oauth/authorize`,
    syncRunNow: (slug: string) => `/api/v1/connectors/${slug}/sync/run-now`,
    assetsAll: (slug: string) => `/api/v1/connectors/${slug}/assets/all`,
    assetsSelect: (slug: string) => `/api/v1/connectors/${slug}/assets/select`,
    asset: (slug: string, assetId: string) =>
      `/api/v1/connectors/${slug}/assets/${assetId}`,
    assetSample: (slug: string, assetId: string) =>
      `/api/v1/connectors/${slug}/assets/${assetId}/sample`,
    detail: (connectionId: string) =>
      `/api/v1/connectors/connections/${connectionId}`,
    testConnection: (connectionId: string) =>
      `/api/v1/connectors/connections/${connectionId}/test`,
    previewCounts: (connectionId: string) =>
      `/api/v1/connectors/connections/${connectionId}/preview-counts`,
    logsByConnection: (connectionId: string) =>
      `/api/v1/connectors/connections/${connectionId}/logs`,
    connectionAssets: (connectionId: string) =>
      `/api/v1/connectors/connections/${connectionId}/assets`,
    schedules: (slug: string) => `/api/v1/connectors/${slug}/schedules`,
    scheduleById: (slug: string, scheduleId: string) =>
      `/api/v1/connectors/${slug}/schedules/${scheduleId}`,
    embedToken: (version: ApiVersion = "v1") =>
      `/api/${version}/connectors/embed-token`,
  },
  contentAssets: {
    liveView: (assetId: string, version: ApiVersion = "v1") =>
      `/api/${version}/content-assets/${assetId}/live-view`,
    liveViewFields: (assetId: string, version: ApiVersion = "v1") =>
      `/api/${version}/content-assets/${assetId}/live-view/fields`,
    /** POST (multipart `file`, PNG or JPEG) sets the view screenshot; DELETE clears it. */
    thumbnail: (assetId: string, version: ApiVersion = "v2") =>
      `/api/${version}/content-assets/${assetId}/thumbnail`,
  },
  kg: {
    entities: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/entities`,
    nodeOverview: (nodeId: string) => `/api/kg/nodes/${nodeId}/overview`,
    dataSourceOverview: (dataSourceId: string) =>
      `/api/v1/kg/data-source/${dataSourceId}/overview`,
    tableOverview: (schemaId: string) => `/api/kg/table/${schemaId}/overview`,
    dataSourceTables: (dataSourceId: string) =>
      `/api/v1/kg/datasources/${dataSourceId}/tables`,
    dataSourceReports: (dataSourceId: string) =>
      `/api/v1/kg/datasources/${dataSourceId}/reports`,
    dataSourceUsedIn: (dataSourceId: string) =>
      `/api/v1/kg/datasources/${dataSourceId}/used-in`,
    schemaRelationships: (schemaId: string) =>
      `/api/kg/schemas/${schemaId}/relationships`,
    schemaMetrics: (schemaId: string) => `/api/kg/schemas/${schemaId}/metrics`,
    schemaUsedIn: (schemaId: string) => `/api/kg/schemas/${schemaId}/used-in`,
    ontologyDetail: (workspaceId: string, ontologyId: string) =>
      `/api/kg/workspaces/${workspaceId}/ontologies/${ontologyId}/detail`,
    ontologyOverview: (ontologyId: string) =>
      `/api/kg/ontologies/${ontologyId}/overview`,
    ontologyLinks: (workspaceId: string, ontologyId: string) =>
      `/api/kg/workspaces/${workspaceId}/ontologies/${ontologyId}/links`,
    ontologyLinkCreate: "/api/kg/ontology-links",
    ontologyLinkDelete: (linkId: string) => `/api/kg/ontology-links/${linkId}`,
    relationVerbs: "/api/kg/relation-verbs",
    ontologyAssociations: (
      workspaceId: string,
      ontologyId: string,
      section: string,
    ) =>
      `/api/kg/workspaces/${workspaceId}/ontologies/${ontologyId}/${section}`,
    ontologyAssociationItem: (
      workspaceId: string,
      ontologyId: string,
      section: string,
      itemId: string,
    ) =>
      `/api/kg/workspaces/${workspaceId}/ontologies/${ontologyId}/${section}/${itemId}`,
    ontologyMetrics: (workspaceId: string, ontologyId: string) =>
      `/api/kg/workspaces/${workspaceId}/ontologies/${ontologyId}/metrics`,
    ontologyRules: (workspaceId: string, ontologyId: string) =>
      `/api/kg/workspaces/${workspaceId}/ontologies/${ontologyId}/rules`,
    ontologyReports: (ontologyId: string) =>
      `/api/kg/ontologies/${ontologyId}/reports`,
    ontologyUsedIn: (ontologyId: string) =>
      `/api/kg/ontologies/${ontologyId}/used-in`,
    metricsList: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/metrics`,
    metricsCreate: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/metrics`,
    metricsUpdate: (workspaceId: string, id: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/metrics/${id}`,
    businessRulesList: (workspaceId: string) =>
      `/api/kg/workspaces/${workspaceId}/business-rules`,
    businessRulesCreate: (workspaceId: string) =>
      `/api/kg/workspaces/${workspaceId}/business-rules`,
    businessRulesUpdate: (workspaceId: string, id: string) =>
      `/api/kg/workspaces/${workspaceId}/business-rules/${id}`,
    businessRulesDelete: (workspaceId: string, id: string) =>
      `/api/kg/workspaces/${workspaceId}/business-rules/${id}`,
    tribalKnowledgeList: (workspaceId: string) =>
      `/api/kg/workspaces/${workspaceId}/tribal-knowledge`,
    tribalKnowledgeCreate: (workspaceId: string) =>
      `/api/kg/workspaces/${workspaceId}/tribal-knowledge`,
    tribalKnowledgeUpdate: (workspaceId: string, id: string) =>
      `/api/kg/workspaces/${workspaceId}/tribal-knowledge/${id}`,
    tribalKnowledgeDelete: (workspaceId: string, id: string) =>
      `/api/kg/workspaces/${workspaceId}/tribal-knowledge/${id}`,
    ontologiesList: (workspaceId: string) =>
      `/api/kg/workspaces/${workspaceId}/ontologies`,
    metricOverview: (metricId: string) =>
      `/api/v1/kg/metrics/${metricId}/overview`,
    metricFormula: (metricId: string) =>
      `/api/v1/kg/metrics/${metricId}/formula`,
    metricUsedIn: (metricId: string) =>
      `/api/v1/kg/metrics/${metricId}/used-in`,
    reportOverview: (reportId: string) =>
      `/api/kg/reports/${reportId}/overview`,
    reportTrace: (reportId: string) => `/api/kg/reports/${reportId}/trace`,
    reportUsedIn: (reportId: string) => `/api/kg/reports/${reportId}/used-in`,
    reportActivity: (reportId: string) =>
      `/api/kg/reports/${reportId}/activity`,
    dataStoryOverview: (dataStoryId: string) =>
      `/api/kg/data-stories/${dataStoryId}/overview`,
    dataStoryTrace: (dataStoryId: string) =>
      `/api/kg/data-stories/${dataStoryId}/trace`,
    dataStoryActivity: (dataStoryId: string) =>
      `/api/kg/data-stories/${dataStoryId}/activity`,
    artifactOverview: (artifactId: string) =>
      `/api/kg/artifacts/${artifactId}/overview`,
    dataModelOverview: (dataModelId: string) =>
      `/api/kg/data-models/${dataModelId}/overview`,
    dataModelTables: (dataModelId: string) =>
      `/api/kg/data-models/${dataModelId}/tables`,
    dataModelMetrics: (dataModelId: string) =>
      `/api/kg/data-models/${dataModelId}/metrics`,
    dataModelRelationships: (dataModelId: string) =>
      `/api/kg/data-models/${dataModelId}/relationships`,
    artifactActivity: (artifactId: string) =>
      `/api/kg/artifacts/${artifactId}/activity`,
    dataSourcesList: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/data-sources`,
    tablesList: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/tables`,
    tablePreview: (workspaceId: string, tableId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/tables/${tableId}/preview`,
    reportsList: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/reports`,
    reportPreview: (workspaceId: string, reportId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/reports/${reportId}/preview`,
    reportSalesforceData: (workspaceId: string, reportId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/reports/${reportId}/salesforce-data`,
    sgmPipelineTrigger: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/sgm/pipeline/trigger`,
    sgmPipelineRunStatus: (workspaceId: string, runId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/sgm/pipeline/runs/${runId}`,
    pipelineRuns: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/kg/pipeline-runs`,
  },
  dataStories: {
    /** GET /api/v1/data-stories/:storyId/builder-config */
    builderConfig: (storyId: string) =>
      `/api/v1/data-stories/${storyId}/builder-config`,
    /** PATCH /api/v1/data-stories/:storyId/builder-config */
    builderConfigPatch: (storyId: string) =>
      `/api/v1/data-stories/${storyId}/builder-config`,
    /** GET /api/v1/data-stories/:storyId/builder-config/stream (SSE) */
    builderConfigStream: (storyId: string) =>
      `/api/v1/data-stories/${storyId}/builder-config/stream`,
    /** POST AI narrative actions for Goal / Purpose */
    goalAction: (
      storyId: string,
      action: "regenerate" | "improve" | "shorten" | "lengthen",
    ) => `/api/v1/data-stories/${storyId}/builder-config/goal/${action}`,
    purposeAction: (
      storyId: string,
      action: "regenerate" | "improve" | "shorten" | "lengthen",
    ) => `/api/v1/data-stories/${storyId}/builder-config/purpose/${action}`,
    /** WS /api/v1/data-stories/:storyId/generate/stream — streams story widgets as they are produced */
    generateStreamWs: (storyId: string) =>
      `/api/v1/data-stories/${storyId}/generate/stream`,
  },

  chat: {
    /** WS /ws/v1/chat/:sessionId — streams chat messages and AI responses */
    messagesStreamWs: (sessionId: string) => `/ws/v1/chat/${sessionId}`,
  },
  dataModels: {
    /**
     * GET list / POST create. The trailing slash is required: without it the
     * backend 307s to an absolute dev URL, which bypasses the local dev proxy.
     */
    list: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/data-models/`,
    catalog: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/data-models/catalog`,
    builder: (workspaceId: string, dataModelId: string) =>
      `/api/v1/workspaces/${workspaceId}/data-models/${dataModelId}?view=builder`,
    table: (workspaceId: string, tableId: string) =>
      `/api/v1/workspaces/${workspaceId}/tables/${tableId}`,
    byId: (workspaceId: string, dataModelId: string) =>
      `/api/v1/workspaces/${workspaceId}/data-models/${dataModelId}`,
    /**
     * GET ?search= — v2 field catalog of a model release, grouped Dates /
     * Dimensions / Geographic / Measures / Calculated (Chart Studio Data
     * models panel). Not workspace-scoped; bare body.
     */
    fieldCatalog: (modelReleaseId: string) =>
      `/api/v2/models/${modelReleaseId}/fields`,
    /** GET — v2 field detail (Chart Studio field-detail card). Not workspace-scoped; bare body. */
    fieldStats: (modelReleaseId: string, fieldId: string) =>
      `/api/v2/models/${modelReleaseId}/fields/${fieldId}`,
    /** GET — a number field's bins and rows in a range, for the filter value editor. Bare body. */
    fieldDistribution: (modelReleaseId: string, fieldId: string) =>
      `/api/v2/models/${modelReleaseId}/fields/${encodeURIComponent(fieldId)}/stats`,
    /** GET — one page of a field's distinct values, for the story filter value menu. Bare body. */
    fieldValues: (modelReleaseId: string, fieldId: string) =>
      `/api/v2/models/${modelReleaseId}/fields/${encodeURIComponent(fieldId)}/values`,
  },
  /** Chart Studio v2 (any Vega chart, built from fields). Not workspace-scoped; bare bodies. */
  chartStudioV2: {
    /** GET — the chart catalog: families, options, types with their slots, 77 gallery entries. */
    catalog: "/api/v2/chart-catalog",
    /** GET — `rel` is a model release id, or `current` for the tenant's current release. */
    modelFields: (rel: string) => `/api/v2/models/${encodeURIComponent(rel)}/fields`,
    /** POST {fields?, look, item_id?} — runs the query and draws it; nothing is stored. */
    chartPreviews: (storyId: string) => `/api/v2/stories/${storyId}/chart-previews`,
    /** POST {look} — re-draws from a preview's rows, with no query. */
    chartPreviewDraw: (storyId: string, previewId: string) =>
      `/api/v2/stories/${storyId}/chart-previews/${previewId}/draw`,
  },
  stories: {
    /** GET (SSE) /api/v2/story/:storyId/events — `snapshot` on connect, then named events. */
    events: (storyId: string) => `/api/v2/story/${storyId}/events`,
    /** GET /api/v1/workspaces/:workspaceId/stories — list stories in a workspace */
    list: (workspaceId: string) => `/api/v1/workspaces/${workspaceId}/stories`,
    /** DELETE /api/v1/workspaces/:workspaceId/stories/:storyId — soft-delete a story (owner only, 204) */
    remove: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}`,
    /** POST /api/v1/workspaces/:workspaceId/stories — create a new story */
    create: (workspaceId: string, version: ApiVersion = "v1") =>
      `/api/${version}/workspaces/${workspaceId}/stories`,
    /**
     * GET (read) / PATCH (title, description, is_auto_refresh) — the story
     * row: status, `publish_status` and everything StoryLayout hands down.
     * Workspace-scoped, unlike the flat `/api/v2/stories/...` routes below.
     * Replaces the retired v1 GET/PATCH on the same path under `/api/v1`.
     */
    v2Meta: (workspaceId: string, storyId: string) =>
      `/api/v2/workspaces/${workspaceId}/stories/${storyId}`,
    /** POST — set `publish_status` to published. Idempotent; returns the story. */
    v2Publish: (workspaceId: string, storyId: string) =>
      `/api/v2/workspaces/${workspaceId}/stories/${storyId}/publish`,
    /** POST — copy the story (layout, charts, filters) into a new draft in the same workspace. 201 with the new story. */
    v2Duplicate: (workspaceId: string, storyId: string) =>
      `/api/v2/workspaces/${workspaceId}/stories/${storyId}/duplicate`,
    /** POST — set `publish_status` back to draft. Idempotent; returns the story. */
    v2Unpublish: (workspaceId: string, storyId: string) =>
      `/api/v2/workspaces/${workspaceId}/stories/${storyId}/unpublish`,
    /** GET /api/v1/workspaces/:workspaceId/stories/:storyId/data-sources — connectors feeding this story */
    dataSources: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/data-sources`,
    /** GET /api/v1/workspaces/:workspaceId/stories/:storyId/members — collaborators on this story */
    members: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/members`,
    /**
     * GET/POST /api/v2/stories/:storyId/invitees — full invite lifecycle
     * (pending/accepted/expired/revoked), distinct from `members` above
     * (confirmed active membership only). Flat path, no workspaceId — the
     * backend derives tenant scope from the story itself.
     */
    invitees: (storyId: string) => `/api/v2/stories/${storyId}/invitees`,
    /**
     * GET (list, filterable) / POST (create root or reply) — comments
     * threaded one level deep (root + replies). Flat path, no workspaceId —
     * same convention as `invitees` above.
     */
    comments: (storyId: string) => `/api/v2/stories/${storyId}/comments`,
    /** GET /api/v2/stories/:storyId/comments/counts — bulk total/unresolved counts, story-wide + per canvas item */
    commentCounts: (storyId: string) =>
      `/api/v2/stories/${storyId}/comments/counts`,
    /** GET (single thread, deep-link) / PATCH (edit, author-only) / DELETE (soft delete, author-only) */
    comment: (storyId: string, commentId: string) =>
      `/api/v2/stories/${storyId}/comments/${commentId}`,
    /** POST — mark a root comment resolved. Idempotent, any story member may call it. */
    commentResolve: (storyId: string, commentId: string) =>
      `/api/v2/stories/${storyId}/comments/${commentId}/resolve`,
    /** POST — clear a root comment's resolved state. Idempotent, same actor rule as resolve. */
    commentRestore: (storyId: string, commentId: string) =>
      `/api/v2/stories/${storyId}/comments/${commentId}/restore`,
    /**
     * GET /api/v2/stories/:storyId/members?search=&limit= — story-scoped
     * member search powering the comment composer's `@`-mention dropdown.
     * Distinct from `members` above (that's the v1 workspace-nested
     * avatar-stack listing, no search, limit defaults to 3).
     */
    memberSearch: (storyId: string) =>
      `/api/v2/stories/${storyId}/members`,
    /**
     * GET ?detail=summary|full — v2 canvas items. Flat, no workspaceId, like
     * `comments`; read alongside the v1 `canvasManifest`, not instead of it.
     */
    items: (storyId: string) => `/api/v2/stories/${storyId}/items`,
    /** POST {source_story_id, source_item_id, title?} — copies a chart of another story (fields and look) into this one. */
    itemsCopy: (storyId: string) => `/api/v2/stories/${storyId}/items/copy`,
    /** POST {title?, position?} — copies a chart or KPI of this story (chat-built or Studio-built) beside the original; 201 with the drawn copy. */
    itemDuplicate: (storyId: string, itemId: string) => `/api/v2/stories/${storyId}/items/${itemId}:duplicate`,
    /** GET, NDJSON: `ids` (comma-separated, at most 50) in one call, a line per item as it is ready. */
    itemsBatch: (storyId: string, ids: readonly string[]) =>
      `/api/v2/stories/${storyId}/items:batch?ids=${ids.join(",")}`,
    /** GET (full item) / PATCH (binding, format — merge-patch) / DELETE (soft, 204). */
    item: (storyId: string, itemId: string) =>
      `/api/v2/stories/${storyId}/items/${itemId}`,
    /** GET — the SQL behind one new-path chart: `{scope: "chart_query", statements, message}`. 409 for any other item. */
    itemSql: (storyId: string, itemId: string) =>
      `/api/v2/stories/${storyId}/items/${itemId}/sql`,
    /** GET — a chart's measures over all its rows, for View data's Total row: `{totals, message}`. */
    itemTotals: (storyId: string, itemId: string) =>
      `/api/v2/stories/${storyId}/items/${itemId}/totals`,
    /** GET — a new-path Story as Story Manager keeps it, with its `layout`. */
    newPathStory: (storyId: string) => `/api/v2/story/${storyId}`,
    /** POST `{edit}` → 202 `{request_id}`: one edit to a new-path Story, applied by Story Manager. */
    edits: (storyId: string) => `/api/v2/story/${storyId}/edits`,
    /** GET — an edit's status: `pending`, `done` or `failed`. */
    edit: (storyId: string, requestId: string) =>
      `/api/v2/story/${storyId}/edits/${requestId}`,
    /**
     * GET ?canvas_item_id=&kind= — story filters: `{can_edit, filters[]}`.
     * Kind is repeatable (`kind=a&kind=b`). Writes join this path in later slices.
     */
    filters: (storyId: string) => `/api/v2/stories/${storyId}/filters`,
    /**
     * PATCH / DELETE one filter. A baseline id looks like `baseline:{item}:{id}`, so
     * it is URL-encoded here.
     */
    filter: (storyId: string, filterId: string) =>
      `/api/v2/stories/${storyId}/filters/${encodeURIComponent(filterId)}`,
    /** POST — rewind the trail to a step, or clear it. Body `{to_filter_id, expected_latest_id}`. */
    filtersRewind: (storyId: string) => `/api/v2/stories/${storyId}/filters/rewind`,
    /** POST, no body — the owner keeps a personal filter for everyone who opens the Story. */
    filterKeep: (storyId: string, filterId: string) =>
      `/api/v2/stories/${storyId}/filters/${encodeURIComponent(filterId)}/keep`,
    /**
     * GET ?canvas_item_id=&kind=&used_only=&include_identifiers=&search= — the fields a
     * filter picker offers: `{fields: StoryField[]}`. Kind is repeatable.
     */
    fields: (storyId: string) => `/api/v2/stories/${storyId}/fields`,
    /** GET/POST/PUT story refresh schedule — one schedule per story. */
    refreshSchedule: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/refresh-schedule`,
    /** POST story "Refresh Now" — one-shot on-demand refresh, returns a job id (202). */
    refreshNow: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/refresh`,
    /** WS /ws/v1/stories/:storyId/canvas — event notifications + status ticker */
    canvasStreamWs: (storyId: string) => `/ws/v1/stories/${storyId}/canvas`,
    /** WS /api/v1/ws/workspaces/:workspaceId/stories/:storyId/activity — live agent activity feed (activity/completed/failed) */
    activityStreamWs: (workspaceId: string, storyId: string) =>
      `/api/v1/ws/workspaces/${workspaceId}/stories/${storyId}/activity`,
    /** GET /api/v1/workspaces/:workspaceId/stories/:storyId/canvas/manifest */
    canvasManifest: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas/manifest`,
    /** GET /api/v1/workspaces/:workspaceId/stories/:storyId/canvas/widget/:widgetId */
    canvasWidget: (workspaceId: string, storyId: string, widgetId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas/widget/${widgetId}`,
    /**
     * GET .../canvas/widget/:widgetId/salesforce-data — Salesforce only.
     * Unlike every other widget type, Salesforce serves no data captured at
     * add-time; this re-fetches the report/dashboard fresh from Salesforce's
     * Reports & Dashboards REST API on every call.
     */
    canvasWidgetSalesforceData: (workspaceId: string, storyId: string, widgetId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas/widget/${widgetId}/salesforce-data`,
    /**
     * GET .../canvas/widget/:widgetId/hubspot-data — HubSpot only, and only
     * for widgets with a stored `hubspot_query`. Re-runs that query fresh
     * against HubSpot's CRM Search API on every call — see
     * hubspot_live_data_service.py. Unrelated to `snapshot_svg`, which the
     * widget also carries and renders as a fallback.
     */
    canvasWidgetHubspotData: (workspaceId: string, storyId: string, widgetId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas/widget/${widgetId}/hubspot-data`,
    /** PATCH .../canvas/widget/:widgetId/hubspot-query — sets/replaces a HubSpot widget's live-query definition. */
    canvasWidgetHubspotQuery: (workspaceId: string, storyId: string, widgetId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas/widget/${widgetId}/hubspot-query`,
    /**
     * POST .../stories/:storyId/canvas-items/content — add a client-authored
     * content item (link/embed/image/text). Returns full widget data (same
     * shape GET canvasWidget returns), not just an id/type/position stub.
     */
    canvasContentItemAdd: (
      workspaceId: string,
      storyId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/canvas-items/content`,
    /** GET /api/v1/workspaces/:workspaceId/stories/:storyId/canvas/datasets/:canvasItemId */
    canvasDataset: (
      workspaceId: string,
      storyId: string,
      canvasItemId: string,
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas/datasets/${canvasItemId}`,
    /** GET /api/v1/workspaces/:workspaceId/canvas-search — search connectors + native charts/kpis to add as a report */
    canvasSearch: (workspaceId: string, version: ApiVersion = "v1") =>
      `/api/${version}/workspaces/${workspaceId}/canvas-search`,
    /** POST /api/v1/workspaces/:workspaceId/stories/:storyId/canvas-items — add a searched asset at its frontend grid position. */
    canvasItemsAdd: (
      workspaceId: string,
      storyId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/canvas-items`,
    /** GET /api/v1/workspaces/:workspaceId/stories/:storyId/chat/messages */
    chatMessages: (
      workspaceId: string,
      storyId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/chat/messages`,
    /** POST /api/v1/workspaces/:workspaceId/stories/:storyId/chat/messages — send a chat message; triggers the story pipeline (status → generating) */
    chatMessagesSend: (
      workspaceId: string,
      storyId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/chat/messages`,
    /** PATCH /api/v1/workspaces/:workspaceId/stories/:storyId/canvas/:canvasId */
    updateCanvas: (workspaceId: string, storyId: string, canvasId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas/${canvasId}`,
    /** PATCH /api/v1/workspaces/:workspaceId/stories/:storyId/canvas/widgets/:widgetId */
    updateCanvasWidget: (
      workspaceId: string,
      storyId: string,
      widgetId: string,
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas/widgets/${widgetId}`,
    /** PATCH /api/v1/workspaces/:workspaceId/stories/:storyId/canvas/widgets/:widgetId/datasets */
    updateCanvasWidgetDatasets: (
      workspaceId: string,
      storyId: string,
      widgetId: string,
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas/widgets/${widgetId}/datasets`,
    /** POST .../stories/:storyId/narrative/generate — (re)generate the story-level narrative */
    storyNarrativeGenerate: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/narrative/generate`,
    /** POST .../stories/:storyId/narrative/improve — AI-improve the story-level narrative */
    storyNarrativeImprove: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/narrative/improve`,
    /** PUT .../stories/:storyId/narrative — manual override of the story-level narrative */
    storyNarrativeUpdate: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/narrative`,
    /** POST .../stories/:storyId/canvas-items/:itemId/narrative/generate — (re)generate a chart/kpi narrative */
    canvasItemNarrativeGenerate: (
      workspaceId: string,
      storyId: string,
      itemId: string,
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}/narrative/generate`,
    /** POST .../stories/:storyId/canvas-items/:itemId/narrative/improve — AI-improve a chart/kpi narrative */
    canvasItemNarrativeImprove: (
      workspaceId: string,
      storyId: string,
      itemId: string,
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}/narrative/improve`,
    /** PUT .../stories/:storyId/canvas-items/:itemId/narrative — manual override of a chart/kpi narrative */
    canvasItemNarrativeUpdate: (
      workspaceId: string,
      storyId: string,
      itemId: string,
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}/narrative`,
    /** POST .../stories/:storyId/summary/generate — (re)generate the story-level summary (200 sync when it exists, 202 async otherwise) */
    storySummaryGenerate: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/summary/generate`,
    /** POST .../stories/:storyId/summary/improve — AI-improve the story-level summary (202 async) */
    storySummaryImprove: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/summary/improve`,
    /** PUT .../stories/:storyId/summary — manual override of the story-level summary */
    storySummaryUpdate: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/summary`,
    /** POST .../stories/:storyId/canvas-items/:itemId/summary/generate — (re)generate a chart/kpi summary (200 sync when confirmed, 202 async otherwise) */
    canvasItemSummaryGenerate: (
      workspaceId: string,
      storyId: string,
      itemId: string,
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}/summary/generate`,
    /** POST .../stories/:storyId/canvas-items/:itemId/summary/improve — AI-improve a chart/kpi summary (202 async) */
    canvasItemSummaryImprove: (
      workspaceId: string,
      storyId: string,
      itemId: string,
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}/summary/improve`,
    /** PUT .../stories/:storyId/canvas-items/:itemId/summary — manual override of a chart/kpi summary */
    canvasItemSummaryUpdate: (
      workspaceId: string,
      storyId: string,
      itemId: string,
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}/summary`,
    /** DELETE .../stories/:storyId/canvas-items/:itemId/narrative — remove ONLY a chart/kpi's attached narrative/summary (the item itself stays) */
    canvasItemNarrativeDelete: (
      workspaceId: string,
      storyId: string,
      itemId: string,
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}/narrative`,
    /** PATCH .../stories/:storyId/canvas-items/update-position — bulk move (1 or N items). */
    canvasItemsUpdatePosition: (
      workspaceId: string,
      storyId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/canvas-items/update-position`,
    /** PATCH .../stories/:storyId/canvas-items/:itemId/content-visibility — toggle summary/narrative visibility flags */
    canvasItemContentVisibility: (
      workspaceId: string,
      storyId: string,
      itemId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}/update-attributes`,
    /** PATCH .../stories/:storyId/canvas-items/:itemId/status — accept/reject a chat-added canvas item */
    canvasItemStatusUpdate: (
      workspaceId: string,
      storyId: string,
      itemId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}/status`,
    /** PATCH .../stories/:storyId/canvas-items/:itemId/content — update a link/embed/image/text item's content/metadata */
    canvasContentItemUpdate: (
      workspaceId: string,
      storyId: string,
      itemId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}/content`,
    /** PATCH .../stories/:storyId/canvas-items/:itemId — re-point a report/ingested_widget/captured_widget item at a different connector asset (bare path mirrors POST .../canvas-items, the create endpoint for sourced items) */
    canvasItemSourceUpdate: (
      workspaceId: string,
      storyId: string,
      itemId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/canvas-items/${itemId}`,
    /** GET /api/v1/workspaces/:workspaceId/stories/:storyId/presentation — slide deck structure */
    presentation: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/presentation`,
    /** POST /api/v1/workspaces/:workspaceId/stories/:storyId/artifacts/memo/export — upload client-rendered PDF, get signed URL */
    artifactMemoExport: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/artifacts/memo/export`,
    /** PATCH .../presentation/slides/:slideId — edit the closing slide's title (null clears) */
    presentationSlide: (workspaceId: string, storyId: string, slideId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/presentation/slides/${slideId}`,
    /** POST .../artifacts/presentation/download — render to pptx/pdf, upload, record artifact, return signed URL (one call, no generate step) */
    artifactPresentationDownload: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/artifacts/presentation/download`,
    /** POST .../artifacts/presentation/prerender — queue the Presentation tab's render in the background (202, no body) */
    artifactPresentationPrerender: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/artifacts/presentation/prerender`,
    /** GET .../artifacts/latest/download — stream bytes of the latest exported artifact (used for Slack delivery) */
    artifactLatestDownload: (
      workspaceId: string,
      storyId: string,
      format = "pdf",
    ) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/artifacts/latest/download?format=${format}`,
    /** POST /api/v1/notifications/channels/slack/debug/send-message — send file to hardcoded Slack channel */
    slackDebugSendMessage: () =>
      `/api/v1/notifications/channels/slack/debug/send-message`,
  },
  notifications: {
    /** GET /api/v1/notifications/channels/:channel/connection — 200 with a null body when not yet connected */
    channelConnection: (channel: string) =>
      `/api/v1/notifications/channels/${channel}/connection`,
    /** GET /api/v1/notifications/channels/slack/oauth/authorize — mode=popup returns {authorize_url} JSON; mode=redirect (default) 307s to Slack */
    slackOauthAuthorize: (
      opts: { returnTo?: string; mode?: "popup" | "redirect" } = {},
    ) => {
      const params = new URLSearchParams();
      if (opts.returnTo) params.set("return_to", opts.returnTo);
      if (opts.mode) params.set("mode", opts.mode);
      const qs = params.toString();
      return `/api/v1/notifications/channels/slack/oauth/authorize${qs ? `?${qs}` : ""}`;
    },
    /** GET /api/v1/notifications/channels/slack/channels — real channel list for the connected workspace */
    slackChannels: () => `/api/v1/notifications/channels/slack/channels`,
    /** POST /api/v1/notifications/channels/slack/messages — multipart: channel_id, text, optional file */
    slackSendMessage: () => `/api/v1/notifications/channels/slack/messages`,
    /** POST /api/v1/notifications/rules — create a delivery rule (manual or scheduled) */
    rules: () => `/api/v1/notifications/rules`,
    /** PATCH/DELETE /api/v1/notifications/rules/:ruleId */
    ruleById: (ruleId: string) => `/api/v1/notifications/rules/${ruleId}`,
    /** GET /api/v1/workspaces/:workspaceId/stories/:storyId/alert-metrics — metric picker for "Alert on condition" */
    alertMetrics: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/alert-metrics`,
    /** POST /api/v1/workspaces/:workspaceId/stories/:storyId/alerts — create a story_alerts row */
    storyAlerts: (workspaceId: string, storyId: string) =>
      `/api/v1/workspaces/${workspaceId}/stories/${storyId}/alerts`,
  },
  storyPlans: {
    /** GET /api/v1/workspaces/:workspaceId/stories/:storyId/plan */
    getByStoryId: (
      workspaceId: string,
      storyId: string,
      version: ApiVersion = "v1",
    ) => `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/plan`,
    /** GET /api/v1/workspaces/:workspaceId/stories/:storyId/plans/:planId */
    getById: (
      workspaceId: string,
      storyId: string,
      planId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/plans/${planId}`,
    /** PUT /api/v1/workspaces/:workspaceId/stories/:storyId/plans/:planId */
    update: (
      workspaceId: string,
      storyId: string,
      planId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/plans/${planId}`,
    /** POST /api/v1/workspaces/:workspaceId/stories/:storyId/plans/:planId/confirm */
    confirm: (
      workspaceId: string,
      storyId: string,
      planId: string,
      version: ApiVersion = "v1",
    ) =>
      `/api/${version}/workspaces/${workspaceId}/stories/${storyId}/plans/${planId}/confirm`,
  } as const,
  storyRuns: {
    /** POST /api/v2/workspaces/:workspaceId/stories/:storyId/runs/current/cancel */
    cancelCurrent: (workspaceId: string, storyId: string) =>
      `/api/v2/workspaces/${workspaceId}/stories/${storyId}/runs/current/cancel`,
  } as const,
  agentRuns: {
    /** GET /api/v1/stories/:storyId/agent-runs/:runId — fetch agent run results */
    getById: (storyId: string, runId: string) =>
      `/api/v1/stories/${storyId}/agent-runs/${runId}`,
  },
  files: {
    upload: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/files/upload`,
    preview: (workspaceId: string, fileId: string) =>
      `/api/v1/workspaces/${workspaceId}/files/${fileId}/preview`,
    list: (workspaceId: string) => `/api/v1/workspaces/${workspaceId}/files`,
  },
  assets: {
    /**
     * POST — multipart upload under a registered scope. Returns an object_key
     * (a storage path), never a URL: the bucket is private and is only ever
     * read back through `content` below.
     */
    upload: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/assets/upload`,
    /**
     * GET — streams the artifact bytes for an object_key. Takes `scope` and
     * `key` query params. Requires the auth header, so it cannot be used as an
     * `<img src>` directly — fetch it as a blob (see services/assets).
     */
    content: (workspaceId: string) =>
      `/api/v1/workspaces/${workspaceId}/assets/content`,
  },
  embed: {
    /** GET /api/v1/embed/:token — story metadata for the embed viewer, no auth */
    story: (token: string) => `/api/v1/embed/${token}`,
    /** POST /api/v2/embed/stories/:storyId/token — mint a new embed token (owner-checked, requires auth) */
    createToken: (storyId: string) => `/api/v2/embed/stories/${storyId}/token`,
  },
  shareLinks: {
    /** GET /api/v2/stories/:storyId/share-links — { link, embed } */
    list: (storyId: string) => `/api/v2/stories/${storyId}/share-links`,
    /** PUT /api/v2/stories/:storyId/share-links/:kind */
    item: (storyId: string, kind: string) => `/api/v2/stories/${storyId}/share-links/${kind}`,
    /** POST /api/v2/stories/:storyId/share-links/:kind:reset */
    reset: (storyId: string, kind: string) => `/api/v2/stories/${storyId}/share-links/${kind}:reset`,
    /** POST /api/v2/share-links/:slug:open — public, no auth */
    open: (slug: string) => `/api/v2/share-links/${slug}:open`,
  },
};

const AUTH_BYPASS_PATHS = new Set<string>([
  API_ENDPOINTS.auth.login,
  API_ENDPOINTS.auth.signup,
  API_ENDPOINTS.auth.verifyEmail,
  API_ENDPOINTS.auth.sendVerification,
  API_ENDPOINTS.auth.forgotPassword,
  API_ENDPOINTS.auth.resetPassword,
  API_ENDPOINTS.auth.magicLink,
  API_ENDPOINTS.auth.magicLinkConsume,
  "/api/v1/embed/",
  "/api/v2/share-links/",
]);

let isRedirectingToLogin = false;

/**
 * Per-request flags for the 401 handling below. `_authRetried` marks the one
 * retry a 401 gets after a renewal; `_skipAuthRenewal` marks the renewal call
 * itself, so it neither waits on a renewal nor triggers one.
 */
type AuthAwareRequestConfig = InternalAxiosRequestConfig & {
  _authRetried?: boolean;
  _skipAuthRenewal?: boolean;
};

export const ACCESS_TOKEN_KEY = "accessToken";

const bearerTokenOf = (config: InternalAxiosRequestConfig | undefined): string | null => {
  const header = String(config?.headers?.Authorization ?? "");
  return header.startsWith("Bearer ") ? header.slice("Bearer ".length) : null;
};

const endSessionAndRedirectToLogin = (): void => {
  safeLocalStorage.removeItem(ACCESS_TOKEN_KEY);
  if (!isRedirectingToLogin && window.location.pathname !== "/login") {
    isRedirectingToLogin = true;
    window.location.assign("/login");
  }
};

export interface ApiEnvelope<T> {
  status_code: number;
  success: boolean;
  message: string;
  data: T;
}

/**
 * Replace an axios error's Blob body with its parsed JSON, in place.
 *
 * Only relevant to `responseType: "blob"` requests, where a 4xx/5xx body
 * arrives as a Blob and so is invisible to `getApiErrorMessage`. Best-effort:
 * a non-JSON or unreadable body is left alone, and the caller falls back to
 * its own message.
 */
const rehydrateBlobErrorBody = async (error: unknown): Promise<void> => {
  const response = (error as { response?: { data?: unknown } } | undefined)
    ?.response;
  if (!response || !(response.data instanceof Blob)) return;

  try {
    response.data = JSON.parse(await response.data.text());
  } catch {
    // Not JSON (or unreadable) — leave the Blob in place rather than masking it.
  }
};

class ApiService {
  private api: AxiosInstance;

  /** Keeps the short-lived tenant token alive; see services/auth/accessTokenRenewal.ts. */
  readonly tokenRenewer: AccessTokenRenewer;

  constructor() {
    this.api = axios.create({
      baseURL: getApiBase(),
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.tokenRenewer = createAccessTokenRenewer({
      readToken: () => safeLocalStorage.getItem(ACCESS_TOKEN_KEY),
      writeToken: (token) => safeLocalStorage.setItem(ACCESS_TOKEN_KEY, token),
      mintToken: async (tenantId, currentToken) => {
        const config = {
          headers: { Authorization: `Bearer ${currentToken}` },
          _skipAuthRenewal: true,
        };
        const response = await this.api.post<{ access_token?: string }>(
          API_ENDPOINTS.auth.selectTenant,
          { tenant_id: tenantId },
          config,
        );
        const minted = response.data?.access_token;
        if (!minted) throw new Error("Token renewal returned no token");
        return minted;
      },
    });

    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const status = error.response?.status;
        const requestUrl = String(error.config?.url ?? "");
        const isAuthBypassRoute = Array.from(AUTH_BYPASS_PATHS).some((path) =>
          requestUrl.includes(path),
        );

        const data = error.response?.data as
          | Record<string, unknown>
          | undefined;
        const backendMessage =
          (data?.error as { message?: string } | undefined)?.message ??
          (typeof data?.detail === "string" ? data.detail : null) ??
          (Array.isArray(data?.detail)
            ? (data.detail as { msg?: string }[])[0]?.msg
            : null) ??
          null;

        // Embed viewers have no session to expire — surface the error
        // in-page (e.g. "invalid/expired link") instead of ever redirecting
        // to /login, which the same 401 would otherwise trigger for the
        // authenticated Build page hitting these same canvas/chat paths.
        //TODO:in feature will re-direct to login and create token or refresh token need to discuss
        if (status === 401 && getEmbedToken()) {
          return Promise.reject(
            Object.assign(new Error(backendMessage ?? "Embed token invalid or expired"), { status }),
          );
        }
        // Only treat this as a session expiry if the request actually carried
        // a token — an unauthenticated request 401ing is expected, not an
        // expired-session event, and shouldn't force a redirect/reload.
        const hadToken = Boolean(error.config?.headers?.Authorization);
        const config = error.config as AuthAwareRequestConfig | undefined;

        // The renewal call's own 401 is reported to the renewer, which decides.
        if (status === 401 && !isAuthBypassRoute && hadToken && !config?._skipAuthRenewal) {
          // One retry per request: renew (single-flight, shared by every
          // request that 401s at the same time), then resend. A request that
          // 401s again, or a renewal that fails, ends the session.
          if (config && !config._authRetried) {
            let retryToken: string | null = null;
            try {
              const current = safeLocalStorage.getItem(ACCESS_TOKEN_KEY);
              // Already renewed since this request was sent: just resend.
              retryToken =
                current && current !== bearerTokenOf(config)
                  ? current
                  : await this.tokenRenewer.renew();
            } catch {
              retryToken = null;
            }
            if (retryToken) {
              config._authRetried = true;
              return this.api.request(config);
            }
          }
          endSessionAndRedirectToLogin();
        }

        const appError = Object.assign(
          new Error(
            backendMessage ??
              `Request failed with status ${status ?? "unknown"}`,
          ),
          {
            status,
            code: (data?.error as { code?: string } | undefined)?.code,
            // Per-row validation errors (422) that getApiErrorDetails reads.
            details: (data?.error as { details?: unknown } | undefined)?.details,
          },
        );
        return Promise.reject(appError);
      },
    );
    this.api.interceptors.request.use(
      async (config: AuthAwareRequestConfig) => {
        const headers = new AxiosHeaders(config.headers);
        const embedToken = getEmbedToken();
        if (embedToken) {
          headers.set("X-Embed-Token", embedToken);
        } else if (config._skipAuthRenewal) {
          // The renewal call carries the exact token being renewed; waiting on
          // the renewer here would wait on itself.
        } else {
          const storedToken = safeLocalStorage.getItem(ACCESS_TOKEN_KEY);
          const accessToken = storedToken
            ? await this.tokenRenewer.tokenForRequest(storedToken)
            : null;
          if (accessToken) {
            headers.set("Authorization", `Bearer ${accessToken}`);
          }
        }
        config.headers = headers;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
  }

  private unwrapEnvelope<T>(envelope: ApiEnvelope<T>): T {
    if (!envelope.success) {
      throw new Error(envelope.message || "Request failed");
    }
    return envelope.data;
  }

  async get<T>(url: string, params?: object): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url, { params });
    return response.data;
  }

  /**
   * GET read as newline-delimited JSON: `onLine` gets each parsed line as it arrives, so a
   * caller can act on the first before the last is written. axios cannot stream in the browser,
   * so this is `fetch` with the same base URL, token (renewed as the interceptor does) and embed
   * token. A non-2xx answer rejects with `status`; the caller decides on a fallback.
   */
  async streamLines<T>(url: string, onLine: (line: T) => void, signal?: AbortSignal): Promise<void> {
    const response = await this.openStream(url, "application/x-ndjson", signal);
    await readNdjson<T>(response.body as ReadableStream<Uint8Array>, onLine);
  }

  /**
   * GET opened as a stream: the `fetch` response with its body unread, carrying the same base
   * URL and renewed token as the interceptor. A non-2xx answer or a missing body rejects with
   * `status`. Callers read the body themselves (NDJSON via `streamLines`, SSE via `useServerEvents`).
   */
  async openStream(url: string, accept: string, signal?: AbortSignal): Promise<Response> {
    const headers: Record<string, string> = { Accept: accept };
    const embedToken = getEmbedToken();
    if (embedToken) {
      headers["X-Embed-Token"] = embedToken;
    } else {
      const stored = safeLocalStorage.getItem(ACCESS_TOKEN_KEY);
      const token = stored ? await this.tokenRenewer.tokenForRequest(stored) : null;
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(`${getApiBase()}${url}`, { headers, signal });
    if (!response.ok || !response.body) {
      throw Object.assign(new Error(`Stream request failed (${response.status})`), {
        status: response.status,
      });
    }
    return response;
  }

  async getEnvelope<T>(url: string, params?: object): Promise<T> {
    const response: AxiosResponse<ApiEnvelope<T>> = await this.api.get(url, {
      params,
    });
    return this.unwrapEnvelope(response.data);
  }

  /**
   * GET returning raw bytes.
   *
   * `params` lets authenticated binary endpoints be queried (e.g. the asset
   * serve endpoint's scope/key): the request interceptor attaches the JWT,
   * which an `<img src>` cannot do, so the caller turns the Blob into an object
   * URL. That keeps the token out of every URL and the storage bucket private.
   */
  async getBlob(url: string, params?: object): Promise<Blob> {
    try {
      const response = await this.api.get(url, {
        params: params ?? undefined,
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      // With responseType "blob" axios hands back the *error* body as a Blob
      // too, so the backend's real reason would reach getApiErrorMessage as an
      // unreadable object. Rehydrate it to JSON here, at the one place that
      // knows the response type, rather than teaching every caller (or the
      // shared error reader) about blobs.
      await rehydrateBlobErrorBody(error);
      throw error;
    }
  }

  /**
   * GET returning raw bytes and the filename the server chose (Content-Disposition), or null.
   * Same blob-error handling as `getBlob`.
   */
  async getBlobWithName(url: string, params?: object): Promise<{ blob: Blob; filename: string | null }> {
    try {
      const response = await this.api.get(url, { params: params ?? undefined, responseType: "blob" });
      const disposition = String(response.headers?.["content-disposition"] ?? "");
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      let filename: string | null = null;
      if (match) {
        try {
          filename = decodeURIComponent(match[1]);
        } catch {
          filename = match[1];
        }
      }
      return { blob: response.data, filename };
    } catch (error) {
      await rehydrateBlobErrorBody(error);
      throw error;
    }
  }

  async post<T>(url: string, data: object, options?: RequestOptions): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data, options);
    return response.data;
  }

  async postEnvelope<T>(url: string, data: object): Promise<T> {
    const response: AxiosResponse<ApiEnvelope<T>> = await this.api.post(
      url,
      data,
    );
    return this.unwrapEnvelope(response.data);
  }

  async postFormData<T>(
    url: string,
    formData: FormData,
    config?: { onUploadProgress?: (e: AxiosProgressEvent) => void },
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...config,
    });
    return response.data;
  }

  async put<T>(url: string, data: object, params?: object, options?: RequestOptions): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data, {
      params: params ?? options?.params ?? undefined,
      headers: options?.headers,
    });
    return response.data;
  }

  async patch<T>(url: string, data: object, options?: RequestOptions): Promise<T> {
    const response: AxiosResponse<T> = await this.api.patch(url, data, options);
    return response.data;
  }

  async patchEnvelope<T>(url: string, data: object): Promise<T> {
    const response: AxiosResponse<ApiEnvelope<T>> = await this.api.patch(
      url,
      data,
    );
    return this.unwrapEnvelope(response.data);
  }

  async delete<T>(url: string, options?: RequestOptions): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url, options);
    return response.data;
  }
}

/** Extra request headers and query params for a write (e.g. `Idempotency-Key`, `?story_id=`). */
export interface RequestOptions {
  headers?: Record<string, string>;
  params?: object;
  responseType?: "blob";
}

export interface LoginPayload {
  email: string;
  password: string;
}

/**
 * `intent: "login"` (the sign-in screen) answers `next_step: "no_account"` for
 * an unknown email instead of creating one. Omitted means "signup".
 */
export type SignupCheckIntent = "signup" | "login";

export interface SignupCheckPayload {
  email: string;
  intent?: SignupCheckIntent;
}

export interface VerifyEmailPayload {
  token: string;
}

export interface SendVerificationPayload {
  email: string;
}

export interface ForgotPasswordPayload {
  email: string;
}

export interface ResetPasswordPayload {
  token: string;
  new_password: string;
}

export interface LoginUser {
  id: string;
  email: string;
  /** Null for an account created without a name (SSO, invites). */
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  is_email_verified: boolean;
  intercom_token: string;
  /** Platform superadmin — unlocks the admin pages (e.g. /admin/signups). */
  is_superadmin?: boolean;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: LoginUser;
}

export interface AuthMessageResponse {
  message?: string;
}

export interface SignupCheckResponse {
  email: string;
  next_step:
    | "verify_email"
    | "setup_account"
    | "enter_password"
    | "oauth_login"
    | "pending_approval"
    | "no_account";
}

/**
 * PATCH /auth/signup. While the beta signup-approval gate is on, the account
 * is held for product-team approval: `next_step` is "pending_approval" and
 * no token is issued. Otherwise it carries a token like login does.
 */
export interface SetupAccountResponse {
  email?: string;
  next_step?: "pending_approval";
  access_token?: string;
}

export interface VerifyEmailResponse {
  email: string;
}

export interface SetupAccountPayload {
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  password: string;
}

export interface BackendConnectorRegistryEntry {
  key?: string;
  connector?: string;
  display_name?: string;
  description?: string;
  domain?: string;
  icon_url?: string;
  /** Backend `connector_category` value, e.g. "warehouse", "bi_tool". */
  category?: string | null;
  status?: string;
  connection?: { id?: string; connection_id?: string } | null;
  instances?: BackendConnectorInstance[] | null;
}

export interface BackendConnectorInstance {
  id?: string | number;
  connector_id?: string;
  connection_id?: string;
  connection_name?: string;
  last_sync?: string;
  created_at?: string;
  status?: string;
  connection_status?: string;
}

export interface BackendAuthTypeOption {
  value: string;
  label: string;
}

export interface BackendConnectorWizardStep {
  value: "credentials" | "test" | "select" | "preview" | "finish";
  label: string;
  title?: string;
  note?: string;
  primary_cta_label?: string;
  skip_cta_label?: string;
  submit_on_click?: boolean;
  allow_skip?: boolean;
}

export interface BackendConnectorWizardBehavior {
  requires_asset_selection?: boolean;
  asset_sync_on_select_enter?: boolean;
  asset_sync_max_depth?: number;
  asset_sync_page_limit?: number;
  preview_page_size_options?: number[];
  ui_variant?: string;
  tree_hints?: Record<string, unknown>;
}

export interface BackendConnectorCatalogSchema {
  key: string;
  display_name: string;
  supported_auth_types: BackendAuthTypeOption[];
  supported_connection_modes: string[];
  supported_sync_strategies: string[];
  wizard_steps?: BackendConnectorWizardStep[] | null;
  wizard_behavior?: BackendConnectorWizardBehavior | null;
  config_schema?: Record<string, unknown> | null;
  auth_schema?: Record<string, unknown> | null;
}

export interface ConnectorConnectRequestPayload {
  connection_name: string;
  auth_type: string;
  environment: string;
  connection_mode: string;
  server_url?: string;
  config_json: Record<string, unknown>;
  credentials_json: Record<string, unknown>;
}

export interface ConnectorConnectResponsePayload {
  connection: {
    id: string;
    connection_name: string;
    connector_type: string;
    auth_type: string;
    environment: string;
    connection_mode: string;
    server_url?: string | null;
    config_json: Record<string, unknown>;
    status: string;
    wizard_step: string;
    health_status: string;
    health_error_message?: string | null;
  };
  test_result: {
    success?: boolean;
    message?: string;
    details?: Record<string, unknown>;
  };
}

export interface ConnectorSyncRunNowResponsePayload {
  id: string;
  status: string;
  rows_ingested?: number | null;
}

export interface ConnectorAssetResponsePayload {
  id: string;
  connector_connection_id: string;
  tenant_id: string;
  parent_asset_id: string | null;
  shape: string;
  asset_type: string;
  name: string;
  display_name: string | null;
  qualified_name: string;
  external_id: string | null;
  depth: number;
  attributes_json: Record<string, unknown>;
  is_selected: boolean;
  is_enabled: boolean;
  discovered_at: string;
  last_refreshed_at: string | null;
  deleted_at: string | null;
  row_count?: number | null;
  column_count?: number | null;
  children?: ConnectorAssetResponsePayload[];
}

export interface ConnectorAssetListResponsePayload {
  items: ConnectorAssetResponsePayload[];
  next_cursor?: string | null;
  /** Offset pagination (connection assets list); absent when using cursor mode. */
  next_page?: number | null;
}

export interface ConnectorAssetSampleResponsePayload {
  asset_id: string;
  qualified_name: string;
  asset_type: string;
  columns: string[];
  rows: Array<Array<unknown>>;
  row_count: number;
  truncated: boolean;
  page: number;
  /** Rows per page — matches FastAPI `limit_rows` on `GET .../assets/{id}/sample`. */
  limit_rows: number;
  max_rows: number;
  next_page: number | null;
}

/** PATCH body for `/connectors/{slug}/assets/{asset_id}` — at least one field required server-side. */
export interface ConnectorAssetPatchPayload {
  is_selected?: boolean;
  is_enabled?: boolean;
}

export interface ConnectorAssetsBulkSelectResponsePayload {
  updated: number;
}

export interface BackendConnectorSyncDetails {
  health?: string | null;
  last_sync?: string | null;
  last_updated?: string | null;
  next_sync?: string | null;
}

export interface BackendConnectorConnectionDetail {
  id: string;
  connection_name: string;
  status: string;
  connection_mode: string;
  health_status: string;
  health_last_checked_at?: string | null;
  health_error_message?: string | null;
  auth_type: string;
  environment: string;
  authentication?: string | null;
  created_by?: string | null;
  created_on: string;
  sync_details?: BackendConnectorSyncDetails | null;
  integration_configs?: Record<string, unknown> | null;
  /** Data Studio preparation state returned by the connection-detail API. */
  data_studio_profile_status?: "not_profiled" | "profiling" | "profiled";
}

export interface BackendConnectorConnectionLogEntry {
  id: string;
  event: string;
  event_type: string;
  status: string;
  message?: string | null;
  occurred_at: string;
}

export interface BackendConnectorConnectionLogPage {
  items: BackendConnectorConnectionLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export const apiService = new ApiService();

export const authApi = {
  checkSignupEmail: (
    payload: SignupCheckPayload,
  ): Promise<SignupCheckResponse> =>
    apiService.postEnvelope<SignupCheckResponse>(
      API_ENDPOINTS.auth.signup,
      payload,
    ),
  setupAccount: (payload: SetupAccountPayload): Promise<SetupAccountResponse> =>
    apiService.patchEnvelope<SetupAccountResponse>(
      API_ENDPOINTS.auth.signup,
      payload,
    ),
  verifyEmail: (payload: VerifyEmailPayload): Promise<VerifyEmailResponse> =>
    apiService.postEnvelope<VerifyEmailResponse>(
      API_ENDPOINTS.auth.verifyEmail,
      payload,
    ),
  sendVerification: (
    payload: SendVerificationPayload,
  ): Promise<AuthMessageResponse> =>
    apiService.postEnvelope<AuthMessageResponse>(
      API_ENDPOINTS.auth.sendVerification,
      payload,
    ),
  login: (payload: LoginPayload): Promise<LoginResponse> =>
    apiService.postEnvelope<LoginResponse>(API_ENDPOINTS.auth.login, payload),
  forgotPassword: (
    payload: ForgotPasswordPayload,
  ): Promise<AuthMessageResponse> =>
    apiService.postEnvelope<AuthMessageResponse>(
      API_ENDPOINTS.auth.forgotPassword,
      payload,
    ),
  resetPassword: (
    payload: ResetPasswordPayload,
  ): Promise<AuthMessageResponse> =>
    apiService.postEnvelope<AuthMessageResponse>(
      API_ENDPOINTS.auth.resetPassword,
      payload,
    ),
  logout: (): Promise<AuthMessageResponse> =>
    apiService.postEnvelope<AuthMessageResponse>(API_ENDPOINTS.auth.logout, {}),
  /** Emails a one-time sign-in link; same 200 reply whether or not one was sent. */
  requestMagicLink: (payload: MagicLinkRequest): Promise<{ email?: string }> =>
    apiService.postEnvelope<{ email?: string }>(
      API_ENDPOINTS.auth.magicLink,
      payload,
    ),
  /** Exchanges a sign-in link's token for a session (the /auth/login reply). */
  consumeMagicLink: (payload: MagicLinkConsumeRequest): Promise<LoginResponse> =>
    apiService.postEnvelope<LoginResponse>(
      API_ENDPOINTS.auth.magicLinkConsume,
      payload,
    ),
};

export const connectorRegistryApi = {
  list: async (): Promise<BackendConnectorRegistryEntry[]> =>
    await apiService.get<BackendConnectorRegistryEntry[]>(
      API_ENDPOINTS.connectors.list,
    ),
};

export const connectorCatalogSchemaApi = {
  get: async (key: string): Promise<BackendConnectorCatalogSchema> =>
    await apiService.get<BackendConnectorCatalogSchema>(
      API_ENDPOINTS.connectors.schema(key),
    ),
};

export const connectorConnectApi = {
  connect: async (
    slug: string,
    payload: ConnectorConnectRequestPayload,
  ): Promise<ConnectorConnectResponsePayload> =>
    await apiService.post<ConnectorConnectResponsePayload>(
      API_ENDPOINTS.connectors.connect(slug),
      payload,
    ),
};

export interface ConnectorOAuthAuthorizeResponsePayload {
  authorize_url: string;
}

export const connectorOAuthApi = {
  /**
   * Begin an in-app OAuth consent flow. Returns the provider authorize URL the
   * SPA should navigate the browser to; the backend callback then mints and
   * persists the refresh_token server-side.
   */
  authorize: async (
    slug: string,
    params: {
      connection_name: string;
      is_sandbox?: boolean;
      /** "popup" → callback returns a self-closing page; else full-page redirect. */
      mode?: "popup" | "redirect";
      /** Google Sheets-specific: the pasted sheet URL when OAuth was triggered
       *  from the "Link to Google sheet" field. Ignored by other connectors. */
      spreadsheet_url?: string;
      /** Google Sheets-specific: "import_once" | "live_sync". Ignored by other connectors. */
      connection_mode?: string;
    },
  ): Promise<ConnectorOAuthAuthorizeResponsePayload> =>
    await apiService.get<ConnectorOAuthAuthorizeResponsePayload>(
      API_ENDPOINTS.connectors.oauthAuthorize(slug),
      params,
    ),
};

export interface ConnectorTestExistingResponsePayload {
  success: boolean;
  message?: string | null;
  details?: Record<string, unknown>;
  connection_id?: string;
}

export const connectorConnectionTestApi = {
  /**
   * Test an already-persisted connection (engine source build + health check)
   * WITHOUT re-submitting credentials. Used by the wizard's Test step for OAuth
   * connections, whose token lives server-side (re-submitting the form would
   * wipe it).
   */
  testExisting: async (
    connectionId: string,
  ): Promise<ConnectorTestExistingResponsePayload> =>
    await apiService.post<ConnectorTestExistingResponsePayload>(
      API_ENDPOINTS.connectors.testConnection(connectionId),
      {},
    ),
};

export interface ConnectorPreviewCountsEntryPayload {
  row_count: number | null;
  column_count: number | null;
}

export interface ConnectorPreviewCountsResponsePayload {
  counts: Record<string, ConnectorPreviewCountsEntryPayload>;
}

export const connectorPreviewCountsApi = {
  /**
   * On-demand row/column counts for the wizard's Preview step (Salesforce).
   * Read-only — does not touch is_selected/config_json or trigger a resync;
   * decoupled from the selection-persistence path so it can't affect other
   * connectors sharing bulkSelect/Import.
   */
  fetch: async (
    connectionId: string,
    objectNames: string[],
  ): Promise<ConnectorPreviewCountsResponsePayload> =>
    await apiService.post<ConnectorPreviewCountsResponsePayload>(
      API_ENDPOINTS.connectors.previewCounts(connectionId),
      { object_names: objectNames },
    ),
  /**
   * Same endpoint, keyed by asset id instead of table name — for Google
   * Sheets, whose sheet tab names are only unique WITHIN one spreadsheet
   * file (two selected sheets can legitimately share a title across
   * different files), so name-keying would return the wrong sheet's count.
   */
  fetchByAssetIds: async (
    connectionId: string,
    assetIds: string[],
  ): Promise<ConnectorPreviewCountsResponsePayload> =>
    await apiService.post<ConnectorPreviewCountsResponsePayload>(
      API_ENDPOINTS.connectors.previewCounts(connectionId),
      { asset_ids: assetIds },
    ),
};

export const connectorSyncApi = {
  runNow: async (slug: string): Promise<ConnectorSyncRunNowResponsePayload> =>
    await apiService.post<ConnectorSyncRunNowResponsePayload>(
      API_ENDPOINTS.connectors.syncRunNow(slug),
      {},
    ),
};

export const connectorAssetsApi = {
  listAll: async (
    slug: string,
    params?: {
      max_depth?: number;
      limit?: number;
      cursor?: string;
      /** Scopes assets to this `connector_connections.id` (must belong to slug). */
      connection_id?: string;
      asset_type?: string;
      include_deleted?: boolean;
      /** "tree" returns nested children on each item; "flat" returns a plain list. */
      view?: "tree" | "flat";
    },
  ): Promise<ConnectorAssetListResponsePayload> =>
    await apiService.get<ConnectorAssetListResponsePayload>(
      API_ENDPOINTS.connectors.assetsAll(slug),
      params,
    ),
  patch: async (
    slug: string,
    assetId: string,
    payload: ConnectorAssetPatchPayload,
  ): Promise<ConnectorAssetResponsePayload> =>
    await apiService.patch<ConnectorAssetResponsePayload>(
      API_ENDPOINTS.connectors.asset(slug, assetId),
      payload,
    ),
  bulkSelect: async (
    slug: string,
    assetIds: string[],
  ): Promise<ConnectorAssetsBulkSelectResponsePayload> =>
    await apiService.patch<ConnectorAssetsBulkSelectResponsePayload>(
      API_ENDPOINTS.connectors.assetsSelect(slug),
      { asset_ids: assetIds },
    ),
  sample: async (
    slug: string,
    assetId: string,
    params?: {
      connection_id?: string;
      page?: number;
      /** Page size — sent as `limit_rows` (backend does not read `page_size`). */
      limit_rows?: number;
    },
  ): Promise<ConnectorAssetSampleResponsePayload> =>
    await apiService.get<ConnectorAssetSampleResponsePayload>(
      API_ENDPOINTS.connectors.assetSample(slug, assetId),
      params,
    ),
};

export const connectorDetailApi = {
  get: async (
    connectionId: string,
  ): Promise<BackendConnectorConnectionDetail> =>
    await apiService.get<BackendConnectorConnectionDetail>(
      API_ENDPOINTS.connectors.detail(connectionId),
    ),
  patchStatus: async (
    connectionId: string,
    payload: { status: "connected" | "disconnected" },
  ): Promise<BackendConnectorConnectionDetail> =>
    await apiService.patch<BackendConnectorConnectionDetail>(
      API_ENDPOINTS.connectors.detail(connectionId),
      payload,
    ),
  delete: async (connectionId: string): Promise<unknown> =>
    await apiService.delete<unknown>(
      API_ENDPOINTS.connectors.detail(connectionId),
    ),
};

/** Connection-scoped assets endpoint. Supports both flat (paginated) and tree (full) modes. */
export const connectorConnectionAssetsApi = {
  list: async (
    connectionId: string,
    params?: {
      is_selected?: boolean;
      /** "tree" → nested children, no pagination; "flat" → paginated list. */
      view?: "tree" | "flat";
      /** Tree-level pruning (and flat `depth <= N` filter). 0=roots only. */
      max_depth?: number;
      page?: number;
      limit_rows?: number;
      page_size?: number;
      limit?: number;
      cursor?: string | null;
      include_deleted?: boolean;
    },
  ): Promise<ConnectorAssetListResponsePayload> =>
    await apiService.get<ConnectorAssetListResponsePayload>(
      API_ENDPOINTS.connectors.connectionAssets(connectionId),
      params,
    ),
};

export const connectorConnectionLogsApi = {
  list: async (
    connectionId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<BackendConnectorConnectionLogPage> =>
    await apiService.get<BackendConnectorConnectionLogPage>(
      API_ENDPOINTS.connectors.logsByConnection(connectionId),
      params,
    ),
};

/** Wire shape for `GET/PUT /connectors/{slug}/schedules` (BE-10). */
export interface ConnectorRefreshScheduleReadPayload {
  id: string;
  connector_connection_id: string;
  asset_id: string;
  sync_strategy: string;
  frequency_unit: string;
  frequency_every_n: number;
  timezone: string;
  watermark_attribute?: string | null;
  repeat_dow?: number[] | null;
  repeat_dom?: number | null;
  refresh_time?: string | null;
  is_enabled: boolean;
  next_run_at?: string | null;
  last_run_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConnectorRefreshScheduleUpsertPayload {
  asset_id: string;
  sync_strategy: string;
  frequency_unit: string;
  frequency_every_n: number;
  timezone: string;
  watermark_attribute?: string | null;
  repeat_dow?: number[] | null;
  repeat_dom?: number | null;
  refresh_time?: string | null;
  is_enabled: boolean;
}

/** Refresh schedules — `sync_routes`: list/create need `slug` + `connectionId`; update/delete need `slug` + `scheduleId`. */
export const connectorSchedulesApi = {
  list: async (
    slug: string,
    connectionId: string,
    params?: { asset_id?: string },
  ): Promise<ConnectorRefreshScheduleReadPayload[]> =>
    await apiService.get<ConnectorRefreshScheduleReadPayload[]>(
      API_ENDPOINTS.connectors.schedules(slug),
      { connection_id: connectionId, ...params },
    ),
  create: async (
    slug: string,
    connectionId: string,
    payload: ConnectorRefreshScheduleUpsertPayload,
  ): Promise<ConnectorRefreshScheduleReadPayload> =>
    await apiService.put<ConnectorRefreshScheduleReadPayload>(
      API_ENDPOINTS.connectors.schedules(slug),
      payload,
      { connection_id: connectionId },
    ),
  update: async (
    slug: string,
    scheduleId: string,
    payload: ConnectorRefreshScheduleUpsertPayload,
  ): Promise<ConnectorRefreshScheduleReadPayload> =>
    await apiService.put<ConnectorRefreshScheduleReadPayload>(
      API_ENDPOINTS.connectors.scheduleById(slug, scheduleId),
      payload,
    ),
  remove: async (slug: string, scheduleId: string): Promise<void> => {
    await apiService.delete<unknown>(
      API_ENDPOINTS.connectors.scheduleById(slug, scheduleId),
    );
  },
};

// ─── Knowledge Graph — inspector drawer (right panel) ─────────────────────────

/** HTTP client for KG drawer tab endpoints. Used by `services/kg/kgDrawer.service.ts`. */
export const kgDrawerHttpApi = {
  getNodeOverview: async (nodeId: string): Promise<KgNodeOverviewResponse> =>
    await apiService.get<KgNodeOverviewResponse>(
      API_ENDPOINTS.kg.nodeOverview(nodeId),
    ),

  getDataSourceOverview: async (
    dataSourceId: string,
  ): Promise<KgNodeOverviewResponse> =>
    await apiService.get<KgNodeOverviewResponse>(
      API_ENDPOINTS.kg.dataSourceOverview(dataSourceId),
    ),

  getTableOverview: async (
    schemaId: string,
  ): Promise<KgTableOverviewResponse> =>
    await apiService.get<KgTableOverviewResponse>(
      API_ENDPOINTS.kg.tableOverview(schemaId),
    ),

  getDataSourceTables: async (
    dataSourceId: string,
    params?: { cursor?: string; limit?: number; search?: string },
  ): Promise<KgDataSourceTablesResponse> =>
    await apiService.get<KgDataSourceTablesResponse>(
      API_ENDPOINTS.kg.dataSourceTables(dataSourceId),
      params,
    ),

  getDataSourceReports: async (
    dataSourceId: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<KgDataSourceReportsResponse> =>
    await apiService.get<KgDataSourceReportsResponse>(
      API_ENDPOINTS.kg.dataSourceReports(dataSourceId),
      params,
    ),

  getDataSourceUsedIn: async (
    dataSourceId: string,
    params?: {
      ontologyCursor?: string;
      storyCursor?: string;
      limit?: number;
    },
  ): Promise<KgDataSourceUsedInResponse> =>
    await apiService.get<KgDataSourceUsedInResponse>(
      API_ENDPOINTS.kg.dataSourceUsedIn(dataSourceId),
      params,
    ),

  getSchemaRelationships: async (
    schemaId: string,
    params?: KgSchemaRelationshipsParams,
  ): Promise<KgSchemaRelationshipsResponse> =>
    await apiService.get<KgSchemaRelationshipsResponse>(
      API_ENDPOINTS.kg.schemaRelationships(schemaId),
      params,
    ),

  getSchemaMetrics: async (
    schemaId: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<KgSchemaMetricsResponse> =>
    await apiService.get<KgSchemaMetricsResponse>(
      API_ENDPOINTS.kg.schemaMetrics(schemaId),
      params,
    ),

  getSchemaUsedIn: async (
    schemaId: string,
    params?: KgSchemaUsedInParams,
  ): Promise<KgDataSourceUsedInResponse> =>
    await apiService.get<KgDataSourceUsedInResponse>(
      API_ENDPOINTS.kg.schemaUsedIn(schemaId),
      params,
    ),

  getOntologyOverview: async (
    ontologyId: string,
  ): Promise<KgOntologyOverviewResponse> =>
    await apiService.get<KgOntologyOverviewResponse>(
      API_ENDPOINTS.kg.ontologyOverview(ontologyId),
    ),

  getOntologyLinks: async (
    ontologyId: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<KgOntologyLinksResponse> =>
    await apiService.get<KgOntologyLinksResponse>(
      API_ENDPOINTS.kg.ontologyLinks(getActiveWorkspaceId(), ontologyId),
      params,
    ),

  getOntologyMetrics: async (
    ontologyId: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<KgOntologyMetricsResponse> =>
    await apiService.get<KgOntologyMetricsResponse>(
      API_ENDPOINTS.kg.ontologyMetrics(getActiveWorkspaceId(), ontologyId),
      params,
    ),

  getOntologyRules: async (
    ontologyId: string,
  ): Promise<KgOntologyRulesResponse> =>
    await apiService.get<KgOntologyRulesResponse>(
      API_ENDPOINTS.kg.ontologyRules(getActiveWorkspaceId(), ontologyId),
    ),

  getOntologyReports: async (
    ontologyId: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<KgOntologyReportsResponse> =>
    await apiService.get<KgOntologyReportsResponse>(
      API_ENDPOINTS.kg.ontologyReports(ontologyId),
      params,
    ),

  getOntologyUsedIn: async (
    ontologyId: string,
    params?: KgSchemaUsedInParams,
  ): Promise<KgDataSourceUsedInResponse> =>
    await apiService.get<KgDataSourceUsedInResponse>(
      API_ENDPOINTS.kg.ontologyUsedIn(ontologyId),
      params,
    ),

  getMetricOverview: async (
    metricId: string,
  ): Promise<KgMetricOverviewResponse> =>
    await apiService.get<KgMetricOverviewResponse>(
      API_ENDPOINTS.kg.metricOverview(metricId),
    ),

  getMetricFormula: async (
    metricId: string,
  ): Promise<KgMetricFormulaResponse> =>
    await apiService.get<KgMetricFormulaResponse>(
      API_ENDPOINTS.kg.metricFormula(metricId),
    ),

  getMetricUsedIn: async (
    metricId: string,
    params?: KgSchemaUsedInParams,
  ): Promise<KgDataSourceUsedInResponse> =>
    await apiService.get<KgDataSourceUsedInResponse>(
      API_ENDPOINTS.kg.metricUsedIn(metricId),
      params,
    ),

  getReportOverview: async (
    reportId: string,
  ): Promise<KgReportOverviewResponse> =>
    await apiService.get<KgReportOverviewResponse>(
      API_ENDPOINTS.kg.reportOverview(reportId),
    ),

  getReportTrace: async (reportId: string): Promise<KgReportTraceResponse> =>
    await apiService.get<KgReportTraceResponse>(
      API_ENDPOINTS.kg.reportTrace(reportId),
    ),

  getReportUsedIn: async (
    reportId: string,
    params?: KgSchemaUsedInParams,
  ): Promise<KgDataSourceUsedInResponse> =>
    await apiService.get<KgDataSourceUsedInResponse>(
      API_ENDPOINTS.kg.reportUsedIn(reportId),
      params,
    ),

  getReportActivity: async (
    reportId: string,
  ): Promise<KgReportActivityResponse> =>
    await apiService.get<KgReportActivityResponse>(
      API_ENDPOINTS.kg.reportActivity(reportId),
    ),

  getDataStoryOverview: async (
    dataStoryId: string,
  ): Promise<KgDataStoryOverviewResponse> =>
    await apiService.get<KgDataStoryOverviewResponse>(
      API_ENDPOINTS.kg.dataStoryOverview(dataStoryId),
    ),

  getDataStoryTrace: async (
    dataStoryId: string,
  ): Promise<KgDataStoryTraceResponse> =>
    await apiService.get<KgDataStoryTraceResponse>(
      API_ENDPOINTS.kg.dataStoryTrace(dataStoryId),
    ),

  getDataStoryActivity: async (
    dataStoryId: string,
  ): Promise<KgDataStoryActivityResponse> =>
    await apiService.get<KgDataStoryActivityResponse>(
      API_ENDPOINTS.kg.dataStoryActivity(dataStoryId),
    ),

  getArtifactOverview: async (
    artifactId: string,
  ): Promise<KgArtifactOverviewResponse> =>
    await apiService.get<KgArtifactOverviewResponse>(
      API_ENDPOINTS.kg.artifactOverview(artifactId),
    ),

  getArtifactActivity: async (
    artifactId: string,
  ): Promise<KgArtifactActivityResponse> =>
    await apiService.get<KgArtifactActivityResponse>(
      API_ENDPOINTS.kg.artifactActivity(artifactId),
    ),

  getDataModelOverview: async (
    dataModelId: string,
  ): Promise<KgDataModelOverviewResponse> =>
    await apiService.get<KgDataModelOverviewResponse>(
      API_ENDPOINTS.kg.dataModelOverview(dataModelId),
    ),

  getDataModelTables: async (
    dataModelId: string,
    params?: { cursor?: string; limit?: number; search?: string },
  ): Promise<KgDataModelTablesResponse> =>
    await apiService.get<KgDataModelTablesResponse>(
      API_ENDPOINTS.kg.dataModelTables(dataModelId),
      params,
    ),

  getDataModelMetrics: async (
    dataModelId: string,
    params?: { cursor?: string; limit?: number; search?: string },
  ): Promise<KgDataModelMetricsResponse> =>
    await apiService.get<KgDataModelMetricsResponse>(
      API_ENDPOINTS.kg.dataModelMetrics(dataModelId),
      params,
    ),

  getDataModelRelationships: async (
    dataModelId: string,
    params?: { cursor?: string; limit?: number; search?: string },
  ): Promise<KgDataModelRelationshipsResponse> =>
    await apiService.get<KgDataModelRelationshipsResponse>(
      API_ENDPOINTS.kg.dataModelRelationships(dataModelId),
      params,
    ),
};

/** HTTP client for KG graph canvas. Used by `services/kg/kgGraphApi.ts`. */
export const kgGraphHttpApi = {
  getEntities: async (
    workspaceId: string,
    params?: KgGraphEntitiesParams,
  ): Promise<KgEntityGraphResponse> =>
    await apiService.get<KgEntityGraphResponse>(
      API_ENDPOINTS.kg.entities(workspaceId),
      params,
    ),
};

/** HTTP client for the organization's UI settings. Used by `services/tenantUi/tenantUiApi.ts`. */
export const tenantUiHttpApi = {
  getSettings: async (): Promise<unknown> =>
    await apiService.get<unknown>(API_ENDPOINTS.tenantUi.settings),
};
